/**
 * Adeqo Filters JavaScript
 * Handles collection filtering, sorting, and AJAX updates
 */

class AdeqoFilters extends HTMLElement {
  constructor() {
    super();
    this.filterForm = this.querySelector('form');
    this.debouncedOnSubmit = this.debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    this.bindEvents();
    this.initStickySidebar();
  }

  /**
   * Sticky filtersidebar die met de scrollrichting meebeweegt.
   *
   * De filterlijst is hoger dan het scherm. Een vaste top-offset laat dan altijd
   * een kant buiten beeld: pin je bovenaan, dan is de onderkant onbereikbaar,
   * pin je onderaan, dan schuift de bovenkant achter de header.
   *
   * Daarom schuift de offset mee met hoeveel je scrollt, geklemd tussen twee
   * grenzen: naar beneden scrollen loopt door tot de onderkant van de lijst in
   * beeld staat, naar boven scrollen brengt de bovenkant meteen weer terug tot
   * net onder de header. Geen tweede scrollbalk, niets afgekapt.
   */
  initStickySidebar() {
    this.sidebar = this.querySelector('.adeqo-collection__sidebar');
    if (!this.sidebar) return;

    this.stickyTop = null;
    this.stickyBounds = null;
    this.stickyZoom = 1;
    this.stickyFrame = null;
    this.lastScrollY = window.scrollY;

    this.measureSticky = this.measureSticky.bind(this);
    this.onStickyScroll = this.onStickyScroll.bind(this);

    this.measureSticky();

    if ('ResizeObserver' in window) {
      // De filtergroepen worden bij elke AJAX-render vervangen en groepen kunnen
      // in- en uitklappen; de hoogte verandert dus ook buiten resize om.
      this.stickyObserver = new ResizeObserver(this.measureSticky);
      this.stickyObserver.observe(this.sidebar);
    }

    window.addEventListener('resize', this.measureSticky);
    window.addEventListener('scroll', this.onStickyScroll, { passive: true });
  }

  /**
   * Bepaalt tussen welke twee offsets de sidebar mag bewegen.
   * maxTop = net onder de header, minTop = onderkant van de lijst in beeld.
   */
  measureSticky() {
    if (!this.sidebar) return;

    // Onder 1024px is de sidebar een overlay-paneel met eigen positionering.
    if (window.innerWidth < AdeqoFilters.SIDEBAR_DESKTOP_MIN_WIDTH) {
      this.stickyBounds = null;
      this.sidebar.style.removeProperty('--sidebar-sticky-top');
      return;
    }

    const zoom = AdeqoFilters.rootZoom();
    const sidebarHeight = this.sidebar.getBoundingClientRect().height / zoom;
    if (!sidebarHeight) return;

    const gap = AdeqoFilters.SIDEBAR_STICKY_GAP;
    const viewportHeight = window.innerHeight / zoom;
    const maxTop = this.measureHeaderHeight(zoom) + gap;
    const minTop = viewportHeight - sidebarHeight - gap;

    this.stickyZoom = zoom;
    this.lastScrollY = window.scrollY;

    // Past de lijst binnen het scherm, dan is er niets om te verschuiven en
    // blijft hij gewoon onder de header hangen.
    if (minTop >= maxTop) {
      this.stickyBounds = null;
      this.setStickyTop(maxTop);
      return;
    }

    this.stickyBounds = { minTop: minTop, maxTop: maxTop };
    const current = this.stickyTop === null ? maxTop : this.stickyTop;
    this.setStickyTop(Math.min(maxTop, Math.max(minTop, current)));
  }

  onStickyScroll() {
    if (!this.stickyBounds || this.stickyFrame) return;

    this.stickyFrame = requestAnimationFrame(() => {
      this.stickyFrame = null;

      const bounds = this.stickyBounds;
      if (!bounds) return;

      const scrollY = window.scrollY;
      // scrollY staat buiten de geschaalde root, de offset erbinnen.
      const delta = (scrollY - this.lastScrollY) / this.stickyZoom;
      this.lastScrollY = scrollY;

      const next = this.stickyTop - delta;
      this.setStickyTop(Math.min(bounds.maxTop, Math.max(bounds.minTop, next)));
    });
  }

  setStickyTop(value) {
    this.stickyTop = value;
    this.sidebar.style.setProperty('--sidebar-sticky-top', `${Math.round(value)}px`);
  }

  /**
   * Hoogte van de header, maar alleen als die meescrollt. Staat de sticky header
   * uit in de sectie-instellingen, dan hoeft er niets onder vandaan gehouden.
   */
  measureHeaderHeight(zoom) {
    const header = document.querySelector('.header-adeqo');
    if (!header) return 0;

    // De sticky positionering staat op de sectie-wrapper, niet op .header-adeqo.
    const wrapper = header.closest('[id^="shopify-section"]') || header;
    const position = getComputedStyle(wrapper).position;
    if (position !== 'sticky' && position !== 'fixed') return 0;

    return wrapper.getBoundingClientRect().height / zoom;
  }

  disconnectedCallback() {
    if (this.stickyObserver) this.stickyObserver.disconnect();
    if (this.stickyFrame) cancelAnimationFrame(this.stickyFrame);
    if (this.measureSticky) window.removeEventListener('resize', this.measureSticky);
    if (this.onStickyScroll) window.removeEventListener('scroll', this.onStickyScroll);
  }

  bindEvents() {
    // Sort dropdown change
    const sortSelect = this.querySelector('[name="sort_by"]');
    if (sortSelect) {
      sortSelect.addEventListener('change', this.onSortChange.bind(this));
    }

    // Filter checkbox/radio changes
    this.querySelectorAll('input[type="checkbox"], input[type="radio"]').forEach((input) => {
      input.addEventListener('change', this.debouncedOnSubmit.bind(this));
    });

    // Price range inputs - listen for change events dispatched by the price-range custom element
    this.querySelectorAll('.adeqo-filter__price-input').forEach((input) => {
      input.addEventListener('change', this.debouncedOnSubmit.bind(this));
    });

    // Clear filter buttons
    this.querySelectorAll('[data-clear-filter]').forEach((button) => {
      button.addEventListener('click', this.onClearFilter.bind(this));
    });

    // Clear all filters button
    const clearAllBtn = this.querySelector('[data-clear-all-filters]');
    if (clearAllBtn) {
      clearAllBtn.addEventListener('click', this.onClearAllFilters.bind(this));
    }

    // Mobile filter toggle (toolbar + sticky button)
    document.querySelectorAll('[data-mobile-filters-toggle]').forEach((toggle) => {
      toggle.addEventListener('click', this.toggleMobileFilters.bind(this));
    });

    // Mobile filter close (X button + overlay background)
    document.querySelectorAll('[data-mobile-filters-close]').forEach((closeEl) => {
      closeEl.addEventListener('click', this.closeMobileFilters.bind(this));
    });
  }

  onSubmitHandler(event) {
    event.preventDefault();

    // Build URL exactly like Impact theme does - no conversion
    const searchParams = new URLSearchParams(new FormData(this.filterForm));

    // Remove empty price filters (like Impact theme)
    ['page', 'filter.v.price.gte', 'filter.v.price.lte'].forEach((param) => {
      if (searchParams.get(param) === '') {
        searchParams.delete(param);
      }
    });

    this.renderPage(searchParams.toString());
  }

  onSortChange(event) {
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set('sort_by', event.target.value);
    this.renderPage(searchParams.toString());
  }

  onClearFilter(event) {
    event.preventDefault();
    const filterParam = event.target.dataset.clearFilter;
    const searchParams = new URLSearchParams(window.location.search);

    // Remove all values for this filter
    const keysToDelete = [];
    for (const [key] of searchParams.entries()) {
      if (key.startsWith(filterParam)) {
        keysToDelete.push(key);
      }
    }
    keysToDelete.forEach((key) => searchParams.delete(key));

    this.renderPage(searchParams.toString());
  }

  onClearAllFilters(event) {
    event.preventDefault();
    const searchParams = new URLSearchParams();

    // Keep sort_by if set
    const currentParams = new URLSearchParams(window.location.search);
    const sortBy = currentParams.get('sort_by');
    if (sortBy) {
      searchParams.set('sort_by', sortBy);
    }

    this.renderPage(searchParams.toString());
  }

  toggleMobileFilters() {
    const overlay = document.querySelector('.adeqo-filter-overlay');
    const stickyBtn = document.querySelector('.adeqo-collection__sticky-filter');
    if (overlay) {
      const isOpen = overlay.classList.toggle('is-open');
      document.body.classList.toggle('overflow-hidden', isOpen);
      if (stickyBtn) stickyBtn.style.display = isOpen ? 'none' : 'flex';
    }
  }

  closeMobileFilters() {
    const overlay = document.querySelector('.adeqo-filter-overlay');
    const stickyBtn = document.querySelector('.adeqo-collection__sticky-filter');
    if (overlay) {
      overlay.classList.remove('is-open');
      document.body.classList.remove('overflow-hidden');
      if (stickyBtn) stickyBtn.style.display = 'flex';
    }
  }

  renderPage(searchParams) {
    const url = `${window.location.pathname}?${searchParams}`;

    // Update URL
    window.history.pushState({ searchParams }, '', url);

    // Show loading state
    this.showLoading();

    // Fetch new content
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        this.renderProductGrid(html);
        this.renderFilters(html);
        this.renderProductCount(html);
        this.updateStickyFilterBadge(html);
        this.hideLoading();
      })
      .catch((error) => {
        console.error('Error fetching filtered results:', error);
        this.hideLoading();
      });
  }

  renderProductGrid(html) {
    // Replace the entire grid wrapper to handle both empty and non-empty states
    const newGridWrapper = html.querySelector('.adeqo-collection__grid-wrapper');
    const currentGridWrapper = document.querySelector('.adeqo-collection__grid-wrapper');

    if (newGridWrapper && currentGridWrapper) {
      currentGridWrapper.innerHTML = newGridWrapper.innerHTML;
    }

    // Re-initialize add to cart buttons
    this.initAddToCartButtons();
  }

  renderFilters(html) {
    // Update filter groups - use innerHTML to ensure custom elements are properly upgraded
    const newFilters = html.querySelector('.adeqo-filters__groups');
    const currentFilters = this.querySelector('.adeqo-filters__groups');

    if (newFilters && currentFilters) {
      currentFilters.innerHTML = newFilters.innerHTML;
    }

    // Update active filters
    const newActiveFilters = html.querySelector('.adeqo-filters__active');
    const currentActiveFilters = this.querySelector('.adeqo-filters__active');

    if (newActiveFilters) {
      if (currentActiveFilters) {
        currentActiveFilters.innerHTML = newActiveFilters.innerHTML;
      } else {
        // Insert at the beginning of the form if it didn't exist
        const form = this.querySelector('form');
        if (form) {
          const activeDiv = document.createElement('div');
          activeDiv.className = 'adeqo-filters__active';
          activeDiv.innerHTML = newActiveFilters.innerHTML;
          form.insertBefore(activeDiv, form.firstChild);
        }
      }
    } else if (currentActiveFilters) {
      currentActiveFilters.remove();
    }

    // Re-bind filter events AFTER a small delay to ensure DOM is ready
    // Use setTimeout to allow custom elements to complete their connectedCallback
    setTimeout(() => {
      this.bindFilterEvents();
    }, 50);
  }

  updateStickyFilterBadge(html) {
    const stickyBtn = document.querySelector('.adeqo-collection__sticky-filter');
    if (!stickyBtn) return;

    // Get the updated badge from server-rendered HTML
    const newBtn = html.querySelector('.adeqo-collection__sticky-filter');
    const newBadge = newBtn ? newBtn.querySelector('.adeqo-collection__sticky-filter-count') : null;
    const currentBadge = stickyBtn.querySelector('.adeqo-collection__sticky-filter-count');

    if (newBadge) {
      if (currentBadge) {
        currentBadge.textContent = newBadge.textContent;
      } else {
        const badge = document.createElement('span');
        badge.className = 'adeqo-collection__sticky-filter-count';
        badge.textContent = newBadge.textContent;
        badge.style.cssText = 'display:flex;align-items:center;justify-content:center;min-width:22px;height:22px;padding:0 6px;background-color:#fff;color:#05B175;font-size:12px;font-weight:700;border-radius:50px;';
        stickyBtn.appendChild(badge);
      }
    } else if (currentBadge) {
      currentBadge.remove();
    }
  }

  renderProductCount(html) {
    const newCount = html.querySelector('.adeqo-collection__count');
    const currentCount = document.querySelector('.adeqo-collection__count');

    if (newCount && currentCount) {
      currentCount.innerHTML = newCount.innerHTML;
    }
  }

  bindFilterEvents() {
    // Re-bind checkbox/radio changes
    this.querySelectorAll('input[type="checkbox"], input[type="radio"]').forEach((input) => {
      input.addEventListener('change', this.debouncedOnSubmit.bind(this));
    });

    // Re-bind price inputs (the price-range custom element handles slider sync)
    this.querySelectorAll('.adeqo-filter__price-input').forEach((input) => {
      input.addEventListener('change', this.debouncedOnSubmit.bind(this));
    });

    // Re-bind clear filter buttons
    this.querySelectorAll('[data-clear-filter]').forEach((button) => {
      button.addEventListener('click', this.onClearFilter.bind(this));
    });

    // Re-initialize accordion toggles
    this.initAccordions();
  }

  initAccordions() {
    this.querySelectorAll('.adeqo-filter__header').forEach((header) => {
      header.addEventListener('click', () => {
        const group = header.closest('.adeqo-filter__group');
        if (group) {
          group.classList.toggle('adeqo-filter__group--open');
        }
      });
    });
  }

  initAddToCartButtons() {
    document.querySelectorAll('.adeqo-product-card__add-to-cart').forEach((button) => {
      button.addEventListener('click', function (e) {
        e.preventDefault();

        const card = this.closest('.adeqo-product-card');
        const quantitySelect = card.querySelector('.adeqo-product-card__quantity-select');
        const variantId = this.dataset.variantId;
        const quantity = quantitySelect ? parseInt(quantitySelect.value) : 1;

        this.disabled = true;
        const originalContent = this.innerHTML;
        this.innerHTML =
          '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="animate-spin"><circle cx="12" cy="12" r="10" stroke-dasharray="32" stroke-dashoffset="12"></circle></svg>';

        window.AdeqoCart.add(
          [{ id: parseInt(variantId), quantity: quantity }],
          { source: 'collection-quick-buy' }
        )
          .then(() => {
            this.disabled = false;
            this.innerHTML =
              '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"></polyline></svg>';

            setTimeout(() => {
              this.innerHTML = originalContent;
            }, 2000);
          })
          .catch((error) => {
            console.error('Error:', error);
            this.disabled = false;
            this.innerHTML = originalContent;
          });
      });
    });
  }

  showLoading() {
    const productGrid = document.querySelector('#product-grid');
    if (productGrid) {
      productGrid.classList.add('adeqo-collection__grid--loading');
    }

    // Add loading overlay if not exists
    let overlay = document.querySelector('.adeqo-collection__loading');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.className = 'adeqo-collection__loading';
      overlay.innerHTML = `
        <div class="adeqo-collection__spinner">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10" stroke-dasharray="32" stroke-dashoffset="12"></circle>
          </svg>
        </div>
      `;
      productGrid.parentNode.insertBefore(overlay, productGrid);
    }
    overlay.classList.add('adeqo-collection__loading--visible');
  }

  hideLoading() {
    const productGrid = document.querySelector('#product-grid');
    if (productGrid) {
      productGrid.classList.remove('adeqo-collection__grid--loading');
    }

    const overlay = document.querySelector('.adeqo-collection__loading');
    if (overlay) {
      overlay.classList.remove('adeqo-collection__loading--visible');
    }
  }

  debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}

// Lucht tussen de sidebar en de header of de onderrand van het scherm, en de
// breakpoint waarboven de sidebar een kolom is in plaats van een overlay-paneel.
// Het getal 1024 hoort bij `@media (max-width: 1023px)` in collection-page-adeqo.css.
AdeqoFilters.SIDEBAR_STICKY_GAP = 24;
AdeqoFilters.SIDEBAR_DESKTOP_MIN_WIDTH = 1024;

/**
 * layout/theme.liquid zet boven 1200px `:root { zoom: 0.9 }` (live, niet in de
 * theme-editor). getBoundingClientRect en window.innerHeight geven geschaalde
 * pixels, maar een top-waarde die we terugschrijven wordt nogmaals geschaald.
 * Alles wordt daarom eerst door deze factor gedeeld.
 */
AdeqoFilters.rootZoom = function () {
  const value = parseFloat(getComputedStyle(document.documentElement).zoom);
  return value > 0 ? value : 1;
};

// Define custom element
customElements.define('adeqo-filters', AdeqoFilters);

// Handle browser back/forward
window.addEventListener('popstate', (event) => {
  if (event.state && event.state.searchParams !== undefined) {
    const filterComponent = document.querySelector('adeqo-filters');
    if (filterComponent) {
      filterComponent.renderPage(event.state.searchParams);
    }
  }
});
